import React, { useState, useEffect } from 'react';
import { Checkbox, Modal, Badge, Button, Textarea, Label, TextInput, Spinner, Dropdown } from 'flowbite-react';
import OpenAI from 'openai';
import { BiChevronDown, BiTrash } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { FiExternalLink } from 'react-icons/fi';
import { updatePost } from './automate/wordpressApis';
import { useContent } from '../context/ContentContext';

function OtherPostCard({ targetUrl, post, isSelected, toggleSupportingArticle, remainingPosts, setRemainingPosts }) {
  const {
    siteCredential
  } = useContent();
  const [open, setOpen] = useState(false);
  const [aiGenerateOpen, setAiGenerateOpen] = useState(false);
  const [apiKey, setApiKey] = useState(process.env.REACT_APP_OPENAI_API_KEY);
  const [model, setModel] = useState('gpt-5-mini');
  const [enableWebSearch, setEnableWebSearch] = useState(false);
  const [linkSuggestions, setLinkSuggestions] = useState(() => formatLinkSuggestions(post.linkSuggestions));
  const [originalContent, setOriginalContent] = useState(formatOriginalContent(post.content));
  const [instructions, setInstructions] = useState(
    `Please generate only the updated article content with link suggestions naturally integrated. Do not include any introductions, explanations, or closing statements—return only the fully updated content. Do not include partial links, and ensure that all added external links are set to nofollow.`
  );
  const [finalQuery, setFinalQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedNewContentIndex, setSelectedNewContentIndex] = useState(null);
  const [showQueryForm, setShowQueryForm] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  // Helper to format link suggestions as requested
  function formatLinkSuggestions(suggestions) {
    if (!suggestions || suggestions.length === 0) return targetUrl;
    return suggestions
      .map(
        (sugg, idx) =>
          `${idx + 1}.\nAnchor Text: ${sugg.anchorText}\nExact Placement: ${sugg.exactPlacement}\nUpdated Paragraph: ${sugg.updatedParagraph}`
      )
      .join('\n\n');
  }

  useEffect(() => {
    setLinkSuggestions(formatLinkSuggestions(post.linkSuggestions));

  }, [post?.linkSuggestions]);

  // Helper to format original content within backticks
  function formatOriginalContent(content) {
    if (!content) return '';
    return `\`\`\`\n${content}\n\`\`\``;
  }

  // Load apiKey and model from localStorage if valid
  // useEffect(() => {
  //   const expiry = localStorage.getItem('internalLinkingStorageExpiry');
  //   const now = Date.now();
  //   if (expiry && now < parseInt(expiry, 10)) {
  //     const storedApiKey = localStorage.getItem('internalLinkingApiKey');
  //     const storedModel = localStorage.getItem('internalLinkingModel');
  //     if (storedApiKey) setApiKey(storedApiKey);
  //     if (storedModel) setModel(storedModel);
  //   } else {
  //     localStorage.removeItem('internalLinkingApiKey');
  //     localStorage.removeItem('internalLinkingModel');
  //     localStorage.removeItem('internalLinkingStorageExpiry');
  //   }
  // }, []);

  useEffect(() => {
    const expiry = localStorage.getItem('internalLinkingStorageExpiry');
    const now = Date.now();

    if (expiry && now < parseInt(expiry, 10)) {
      const storedApiKey = localStorage.getItem('internalLinkingApiKey');
      const storedModel = localStorage.getItem('internalLinkingModel');
      if (storedApiKey) {
        setApiKey(storedApiKey);
      } else {
        const envApiKey = process.env.REACT_APP_OPENAI_API_KEY;
        if (envApiKey) setApiKey(envApiKey);
      }
      if (storedModel) setModel(storedModel);
    } else {
      localStorage.removeItem('internalLinkingApiKey');
      localStorage.removeItem('internalLinkingModel');
      localStorage.removeItem('internalLinkingStorageExpiry');

      const envApiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (envApiKey) setApiKey(envApiKey);
    }
  }, []);

  // Save apiKey and model to localStorage with expiry on change
  useEffect(() => {
    if (apiKey && model) {
      localStorage.setItem('internalLinkingApiKey', apiKey);
      localStorage.setItem('internalLinkingModel', model);
      localStorage.setItem('internalLinkingStorageExpiry', (Date.now() + 3 * 60 * 60 * 1000).toString());
    }
  }, [apiKey, model]);

  // Clear localStorage on tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('internalLinkingApiKey');
      localStorage.removeItem('internalLinkingModel');
      localStorage.removeItem('internalLinkingStorageExpiry');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Update final query whenever dependent fields change
  useEffect(() => {
    const buildFinalQuery = () => {
      let sections = [];
      if (linkSuggestions.trim()) {
        sections.push('Link Suggestions: \n' + linkSuggestions);
      }
      if (originalContent.trim()) {
        sections.push('Original Content: \n' + originalContent);
      }
      if (instructions.trim()) {
        sections.push('Instructions: \n' + instructions);
      }
      return sections.join('\n\n');
    };
    setFinalQuery(buildFinalQuery());
  }, [linkSuggestions, originalContent, instructions]);

  // Update aiResponse and selectedNewContentIndex when post.newContents changes
  useEffect(() => {
    if (post.newContents && post.newContents.length > 0) {
      if (selectedNewContentIndex === null) {
        setSelectedNewContentIndex(0);
        setAiResponse(post.newContents[0]);
      } else if (post.newContents[selectedNewContentIndex]) {
        setAiResponse(post.newContents[selectedNewContentIndex]);
      }
    } else {
      setSelectedNewContentIndex(null);
      setAiResponse('');
    }
  }, [post.newContents]);

  const handleOpen = () => {
    setOpen(true);
    setAiResponse('');
  };
  const handleClose = () => setOpen(false);
  const handleAiGenerateOpen = () => {
    setAiGenerateOpen(true);
  };
  const handleAiGenerateClose = () => {
    setAiGenerateOpen(false);
    setShowQueryForm(false);
  };

  const updatePostNewContents = (url, newContent, deleteIndex = null) => {
    setRemainingPosts((prevPosts) => {
      return prevPosts.map((p) => {
        if (p.url === url) {
          let updatedNewContents = p.newContents ? [...p.newContents] : [];
          if (deleteIndex !== null) {
            updatedNewContents.splice(deleteIndex, 1);
          } else if (newContent) {
            updatedNewContents.push(newContent);
          }
          return { ...p, newContents: updatedNewContents, linkSuggestions: p.linkSuggestions || [] };
        }
        return p;
      });
    });
  };

  const handleAiCall = async () => {
    if (!apiKey.trim()) {
      alert('API Key is required.');
      return;
    }
    if (!finalQuery.trim()) {
      alert('Final query cannot be empty.');
      return;
    }
    setLoading(true);
    setAiResponse('');
    try {
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
      });
      const response = await openai.responses.create({
        model: model,
        input: finalQuery,
        ...(enableWebSearch ? { tools: [{ type: "web_search_preview" }] } : {})
      });
      const outputText = response.output_text || 'No output from AI.';
      updatePostNewContents(post.url, outputText);
      setSelectedNewContentIndex(post.newContents ? post.newContents.length : 0);
      setAiGenerateOpen(false);
      setOpen(true);
    } catch (error) {
      setAiResponse('Error generating AI response: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectNewContent = (index) => {
    setSelectedNewContentIndex(index);
    if (post.newContents && post.newContents[index]) {
      setAiResponse(post.newContents[index]);
    }
  };

  const handleDeleteNewContent = (index, e) => {
    e.stopPropagation();
    updatePostNewContents(post.url, null, index);
    if (selectedNewContentIndex === index) {
      setSelectedNewContentIndex(null);
      setAiResponse('');
    }
  };

  return (
    <>
      <div
        className="relative border rounded flex items-center gap-4 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={handleOpen}
      >
        {post.linkSuggestions && post.linkSuggestions.length > 0 && (
          <Badge className="absolute top-1 right-1 z-10 inline-flex items-center py-0.5 px-1.5 rounded-full text-xs font-medium bg-teal-600 text-white">
            {post.linkSuggestions.length}
          </Badge>
        )}
        {post.newContents?.length > 0 &&
          <Badge className="absolute top-6.5 right-1 z-10 inline-flex items-center py-0.5 px-1.5 rounded-full text-xs font-medium bg-pink-600 text-white">
            {post.newContents.length}
          </Badge>
        }
        <div className="relative w-20 h-20 flex-shrink-0">
          <img
            src={post.featured_media_url}
            alt={post.title}
            className="w-full h-full object-cover rounded"
          />
          <div className="absolute top-1 left-1">
            <Checkbox
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleSupportingArticle(post.url)}
              aria-label={`Select supporting article: ${post.title}`}
            />
          </div>
        </div>
        <div className="p-1 flex items-center">
          <h4
            className="text-base font-semibold overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {post.title}
          </h4>
        </div>
      </div>

      <Modal show={open} onClose={handleClose} size="6xl">
        <Modal.Header className='ps-3'>{post.title}</Modal.Header>
        <Modal.Body>
          {post.linkSuggestions && post.linkSuggestions.length > 0 && (
            <div className="mb-4 overflow-auto max-h-60">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-1 text-left text-sm">Priority</th>
                    <th className="border px-2 py-1 text-left text-sm">Anchor Text</th>
                    <th className="border px-2 py-1 text-left text-sm">Exact Placement</th>
                    <th className="border px-2 py-1 text-left text-sm">Updated Paragraph</th>
                  </tr>
                </thead>
                <tbody>
                  {post.linkSuggestions.map((suggestion, index) => (
                    <tr key={index} className="border-t">
                      <td className="border px-2 py-1 text-sm">{suggestion.priority}</td>
                      <td className="border px-2 py-1 text-sm">{suggestion.anchorText}</td>
                      <td className="border px-2 py-1 text-sm">{suggestion.exactPlacement}</td>
                      <td className="border px-2 py-1 text-sm">{suggestion.updatedParagraph}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              {!loading ?
                <Button onClick={handleAiGenerateOpen} >
                  Generate content with link
                </Button>
                :
                <Button>
                  <Spinner aria-label="Info spinner example" className="me-3" light />
                  Generating content
                </Button>
              }
              <div className='flex gap-3'>
                {post.newContents && post.newContents.length > 0 && (
                  <Dropdown label={`New Articles (${post.newContents.length})`} inline={true}>
                    {post.newContents.map((content, index) => (
                      <Dropdown.Item key={index} onClick={() => handleSelectNewContent(index)}>
                        <div className="flex justify-between items-center w-full">
                          <span>Article {index + 1}</span>
                          <BiTrash color='red' size={18} onClick={(e) => handleDeleteNewContent(index, e)} />
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown>
                )}
                <Button>
                  <Link to={post.url} target='_blank' className='flex'>Edit <FiExternalLink size={16} className='ms-2' /></Link>
                </Button>

                <Button
                  onClick={async () => {
                    if (!siteCredential.username || !siteCredential.password) {
                      alert('Credentials are missing');
                      return;
                    }

                    if (!aiResponse || !post.id) {
                      alert('Nothing to save');
                      return;
                    }

                    const confirmed = window.confirm(
                      "Are you sure you want to update this post with the generated content?"
                    );

                    if (!confirmed) return; // ❌ Stop if user cancels

                    setIsSaving(true);
                    try {
                      const result = await updatePost(siteCredential, post.id, { content: aiResponse });

                      if (result.success) {
                        alert(result.message);
                      } else {
                        alert(`Error: ${result.message}`);
                      }
                    } catch (error) {
                      console.error('Failed to save:', error);
                      alert('Failed to save changes');
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  // disabled={
                  //   isSaving ||
                  //   !siteCredential.username ||
                  //   !siteCredential.password ||
                  //   !aiResponse ||
                  //   !post?.id
                  // }
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-1/2 border p-2 overflow-auto max-h-[400px] prose" dangerouslySetInnerHTML={{ __html: post.content || 'No content available.' }} />
              {/* <div className="w-1/2 border p-2 overflow-auto max-h-[400px] prose whitespace-pre-wrap"> */}
              <div className="w-1/2 border p-2 overflow-auto max-h-[400px] prose" dangerouslySetInnerHTML={{ __html: aiResponse || 'No content available.' }} />
              {/* </div> */}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={aiGenerateOpen} onClose={handleAiGenerateClose} size={showQueryForm ? "4xl" : "lg"} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-4">
            <div className='bg-gray-200 py-2 px-3 rounded-lg font-medium cursor-pointer hover:bg-gray-300' onClick={() => setShowQueryForm((curr) => !curr)}>
              {showQueryForm ? "Hide" : "Show"} Query Form
            </div>
            {showQueryForm &&
              <div>
                <Label htmlFor="apiKey" className="block mb-1 font-semibold mt-3">
                  OpenAI API Key *
                </Label>
                <TextInput
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your OpenAI API key"
                  required
                />
                <Label htmlFor="model" className="block mb-1 font-semibold mt-3">
                  Model * (gpt-4.1, gpt-4.1-mini, gpt-5, gpt-5-mini)
                </Label>
                <TextInput
                  id="model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. gpt-4.1"
                  required
                />
                <div className="flex items-center gap-2 mt-3">
                  <input
                    id="enableWebSearch"
                    type="checkbox"
                    checked={enableWebSearch}
                    onChange={(e) => setEnableWebSearch(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="enableWebSearch" className="font-semibold">
                    Enable Web Search
                  </Label>
                </div>
                <Label htmlFor="linkSuggestions" className="block mb-1 font-semibold mt-3">
                  Link Suggestions
                </Label>
                <Textarea
                  id="linkSuggestions"
                  rows={6}
                  value={linkSuggestions}
                  onChange={(e) => setLinkSuggestions(e.target.value)}
                  className="resize-none"
                />
                <Label htmlFor="originalContent" className="block mb-1 font-semibold mt-3">
                  Original Content
                </Label>
                <Textarea
                  id="originalContent"
                  rows={6}
                  value={originalContent}
                  onChange={(e) => setOriginalContent(e.target.value)}
                  className="resize-none"
                />
                <Label htmlFor="instructions" className="block mb-1 font-semibold mt-3">
                  Instructions
                </Label>
                <Textarea
                  id="instructions"
                  rows={4}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  className="resize-none"
                />
                <Label htmlFor="finalQuery" className="block mb-1 font-semibold mt-3">
                  Final Query (Markdown, editable)
                </Label>
                <Textarea
                  id="finalQuery"
                  rows={8}
                  value={finalQuery}
                  onChange={(e) => setFinalQuery(e.target.value)}
                  className="resize-none"
                />
              </div>
            }
            <div className='flex justify-center'>
              <Button onClick={handleAiCall} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Generate'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OtherPostCard;
